import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import AddressAutocomplete from "../components/AddressAutocomplete";
import SatellitePriceLoader from "../components/SatellitePriceLoader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Scissors, Leaf, Trees, Trash2, Layers, Sprout, Droplets, Wrench,
  ShieldCheck, MapPin, Lock, Star, CheckCircle2, ChevronRight,
  ArrowRight, Loader2, Home as HomeIcon, DollarSign, CalendarDays,
} from "lucide-react";

/**
 * Home — iter233 (2026-03-02): Conversion-focused homepage.
 *
 * Owner directive:
 *   "Get Your Lawn Care Price in Seconds. Simple quote form: address →
 *    contact → service → frequency → additional info → SEE MY PRICE.
 *    Every visitor who submits contact info is saved as a Lead in
 *    /admin/leads even if they never book. Do not require account
 *    creation just to receive a quote."
 *
 * Uses the existing pricing engine (POST /api/quote/request) which
 * dedupes leads within 30 days and returns `{lead_id, quote_amount,
 * next_url}`. `next_url` deep-links into /book with prefills so the
 * customer never re-enters anything.
 */

const pubApi = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: false,
});

const SERVICES = [
  { key: "lawn_mowing",     name: "Lawn Mowing",         icon: Scissors, blurb: "Weekly, bi-weekly, or one-time cuts", instant: true },
  { key: "bush_trimming",   name: "Bush / Hedge Trimming", icon: Leaf, blurb: "Shape, size, and remove overgrowth" },
  { key: "tree_trimming",   name: "Tree Trimming",        icon: Trees, blurb: "Small-to-medium trees pruned safely" },
  { key: "yard_cleanup",    name: "Yard Cleanup",         icon: Trash2, blurb: "Leaves, sticks, debris — hauled away" },
  { key: "mulching",        name: "Mulching",             icon: Layers, blurb: "Fresh mulch, weed prep, bed shaping" },
  { key: "pressure_washing",name: "Pressure Washing",     icon: Droplets, blurb: "Driveways, siding, walkways" },
  { key: "sodding",         name: "Sodding",              icon: Sprout, blurb: "Fresh sod install + prep" },
  { key: "landscaping",     name: "Landscaping",          icon: Wrench, blurb: "Custom design + planting" },
];

const FREQ_OPTIONS = [
  { key: "one_time",  label: "One-time" },
  { key: "weekly",    label: "Weekly" },
  { key: "biweekly",  label: "Every 2 weeks" },
  { key: "monthly",   label: "Monthly" },
];

const TRUST = [
  { icon: Lock,        label: "Your info is secure" },
  { icon: ShieldCheck, label: "Fully Insured" },
  { icon: HomeIcon,    label: "Locally Owned" },
  { icon: MapPin,      label: "Jacksonville, FL" },
];

export default function Home() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1 addr · 2 contact · 3 service · 4 result
  const [addr, setAddr] = useState("");
  const [addrDetails, setAddrDetails] = useState(null); // {square_feet, ...}
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [serviceKey, setServiceKey] = useState("lawn_mowing");
  const [frequency, setFrequency] = useState("biweekly");
  const [notes, setNotes] = useState("");
  // iter236 — Customer-chosen service start date. Defaults to +2 days so
  // the crew has scheduling buffer. Used as the FIRST service date and,
  // for recurring frequencies, the anchor for every future visit.
  const _defaultDate = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 2);
    return d.toISOString().slice(0, 10);
  })();
  const _todayIso = new Date().toISOString().slice(0, 10);
  const [serviceDate, setServiceDate] = useState(_defaultDate);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  // iter237 — Satellite-precision loader state. `loading` is true while
  // the 11s "sizing your lawn" overlay is on screen. `apiResult` holds
  // the /quote/request response so we don't reveal it until the loader
  // completes (or fail-open earlier if API takes >11s).
  const [loaderVisible, setLoaderVisible] = useState(false);
  const [loaderFinished, setLoaderFinished] = useState(false);
  const [pendingResult, setPendingResult] = useState(null);
  const [reviews, setReviews] = useState({ rating: 5.0, count: null });

  // Fetch reviews rating for the trust strip
  useEffect(() => {
    (async () => {
      try {
        const { data } = await pubApi.get("/reviews");
        const arr = Array.isArray(data?.reviews) ? data.reviews : [];
        if (arr.length) {
          const avg = arr.reduce((a, r) => a + Number(r.rating || 5), 0) / arr.length;
          setReviews({ rating: Number(avg.toFixed(1)), count: arr.length });
        }
      } catch { /* silent — trust strip still renders */ }
    })();
  }, []);

  const step1Valid = useMemo(() => addr.trim().length > 5, [addr]);
  const step2Valid = useMemo(() => {
    if (!name.trim()) return false;
    const p = phone.replace(/\D/g, "");
    if (p.length < 10) return false;
    if (!/\S+@\S+\.\S+/.test(email)) return false;
    return true;
  }, [name, phone, email]);

  const activeSvc = SERVICES.find((s) => s.key === serviceKey) || SERVICES[0];

  const submitQuote = async () => {
    if (!step2Valid) return;
    // iter237 — Kick off the 11-second satellite loader IMMEDIATELY so
    // the customer sees their actual property while the price is
    // computed. The API call fires in parallel; we only reveal Step 4
    // after BOTH have completed.
    setLoaderVisible(true);
    setLoaderFinished(false);
    setPendingResult(null);
    setSubmitting(true);
    try {
      const { data } = await pubApi.post("/quote/request", {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        phone: phone.replace(/\D/g, ""),
        address: addr,
        service_key: serviceKey,
        frequency,
        square_feet: addrDetails?.square_feet || undefined,
        additional_info: notes.trim() || undefined,
        // iter238 — send the chosen service date so the wizard's Confirm
        // & Pay row shows it (and follow-up emails can reference it).
        preferred_date: serviceDate || undefined,
        utm_source: new URLSearchParams(window.location.search).get("utm_source") || undefined,
        referrer: document.referrer || undefined,
        user_agent: navigator.userAgent,
      });
      setPendingResult(data);
    } catch (e) {
      // Fail-open: dismiss the loader immediately on error.
      setLoaderVisible(false);
      setLoaderFinished(false);
      toast.error(
        e?.response?.data?.detail ||
        "We couldn't calculate your price right now — please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  // iter237 — Reveal the price only after both the API call has returned
  // AND the 11-second satellite loader has finished, so the customer
  // always sees the full experience.
  useEffect(() => {
    if (loaderVisible && loaderFinished && pendingResult) {
      setResult(pendingResult);
      setPendingResult(null);
      setLoaderVisible(false);
      setLoaderFinished(false);
      setStep(4);
      // iter242 — Snap to top of the quote card so the price + Continue
      // CTA are visible in the first viewport without scrolling.
      requestAnimationFrame(() => {
        const el = document.querySelector('[data-testid="home-quote-card"]');
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      });
    }
  }, [loaderVisible, loaderFinished, pendingResult]);

  const continueToBook = () => {
    if (!result?.next_url) return;
    // iter235/iter238 — Skip the "How would you like to get a price?"
    // step and land the customer directly on the Confirm Booking & Pay
    // page. Backend now returns `/book/legacy?...` (previously `/book?`).
    // Match BOTH prefixes so the estimate/contact stash below always
    // runs when routing to the wizard.
    const goesToWizard =
      result.next_url.startsWith("/book?") ||
      result.next_url.startsWith("/book/legacy?");
    if (!goesToWizard) {
      navigate(result.next_url);
      return;
    }
    // (b) Stash a full estimate payload in sessionStorage so Book.jsx can
    //     restore the exact price/calc + property + contact fields and
    //     jump straight to step 3. iter239 (2026-08-22) — ONLY stash the
    //     estimate when we have a concrete positive quote_amount. On the
    //     free-text address path the backend returns a range quote and
    //     quote_amount is null; stashing calc.total=0 would then win over
    //     Book.jsx's /api/estimates/calculate recompute and render
    //     '$0.00' on Confirm & Pay while Stripe charges the real price.
    //     We ALWAYS stash the contact payload (name/email/phone/date) so
    //     the wizard doesn't re-ask for those.
    const stashableTotal = Number(result.quote_amount || 0);
    try {
      const sqft = addrDetails?.square_feet || 4000;
      if (stashableTotal > 0) {
        const calc = {
          total: stashableTotal,
          subtotal: stashableTotal,
          service_name: result.service_name || activeSvc?.name || "Lawn Mowing",
          service_key: result.service_key,
        };
        sessionStorage.setItem("jdsn_pending_estimate", JSON.stringify({
          serviceKey: result.service_key,
          sqft,
          opts: {},
          frequency: result.frequency || frequency,
          yardArea: "both",
          calc,
          areaAlreadyScaled: false,
          grassHeight: "under_9",
          grassCoverage: "full",
          overgrownOverride: "",
          gate_width: "",
          property: addrDetails && addr ? {
            formatted_address: addr,
            square_feet: sqft,
            source: "home_quote",
          } : null,
          savedAt: Date.now(),
        }));
      } else {
        // No firm price — clear any stale stash so Book.jsx recomputes
        // via /api/estimates/calculate from the URL params below.
        sessionStorage.removeItem("jdsn_pending_estimate");
      }
      sessionStorage.setItem("jdsn_pending_contact", JSON.stringify({
        name: name.trim(),
        email: email.trim().toLowerCase(),
        phone: phone.replace(/\D/g, ""),
        address: addr,
        preferred_date: serviceDate,
        preferred_time: "",
        savedAt: Date.now(),
      }));
    } catch { /* sessionStorage may be blocked — Book.jsx falls back to URL params */ }
    // (c) Build full URL params so Book.jsx's existing recompute path
    //     also works if sessionStorage is unavailable, and append the
    //     iter235 `skip_to_pay=1` flag to jump straight to step 3.
    const sqft = addrDetails?.square_feet || 4000;
    const qs = new URLSearchParams({
      service: result.service_key,
      sqft: String(sqft),
      frequency: result.frequency || frequency,
      yard_area: "both",
      skip_to_pay: "1",
      lead_id: result.lead_id,
      addr: addr,
      name: name.trim(),
      email: email.trim().toLowerCase(),
      phone: phone.replace(/\D/g, ""),
      preferred_date: serviceDate,
    });
    if (result.quote_amount) qs.set("price", Number(result.quote_amount).toFixed(2));
    // iter235 — `/book` shows the two-path Smart Quote landing page;
    // /book/legacy renders the actual multi-step wizard where the
    // Confirm Booking & Pay flow lives. Route to /book/legacy so the
    // customer skips the "How would you like to get a price?" page.
    navigate(`/book/legacy?${qs.toString()}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-white pb-16"
          data-testid="home-page">
      {loaderVisible && (
        <SatellitePriceLoader
          lat={addrDetails?.lat}
          lng={addrDetails?.lng}
          address={addr}
          durationMs={6000}
          onComplete={() => setLoaderFinished(true)}
        />
      )}
      {/* HERO */}
      <section className={`max-w-6xl mx-auto px-4 sm:px-6 ${step === 4 ? "pt-3 sm:pt-14" : "pt-8 sm:pt-14"}`}>
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          {/* Left column — hero copy. iter242 (2026-08-22): Hidden on mobile
              at Step 4 so the price card + Continue-to-book CTA fit in the
              first viewport without any scrolling. */}
          <div className={step === 4 ? "hidden lg:block" : ""}>
            <div className="inline-flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-[0.2em] text-emerald-800"
                  data-testid="home-brand-badge">
              <ShieldCheck className="w-3 h-3" />
              JDSN Lawn Care · Jacksonville, FL
            </div>
            <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-gray-900 font-[Outfit]"
                 data-testid="home-headline">
              Get Your Lawn Care Price <span className="text-emerald-700">in Seconds</span>
            </h1>
            <p className="mt-4 text-base sm:text-lg text-gray-600 leading-relaxed max-w-xl">
              Instant quotes on lawn mowing plus every other yard service in
              Jacksonville — mowing, trimming, mulching, pressure washing,
              landscaping and more. No account needed to see your price.
            </p>
            {/* Trust strip — mobile & desktop */}
            <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-[13px] text-gray-700"
                  data-testid="home-trust-strip">
              {TRUST.map((t) => (
                <div key={t.label} className="inline-flex items-center gap-1.5 font-semibold">
                  <t.icon className="w-4 h-4 text-emerald-700" />
                  {t.label}
                </div>
              ))}
              <div className="inline-flex items-center gap-1.5 font-semibold"
                    data-testid="home-google-reviews">
                <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                {reviews.rating.toFixed(1)}
                {reviews.count != null && (
                  <span className="text-gray-500 font-medium">
                    &nbsp;({reviews.count} reviews)
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Right column — quote card */}
          <div className="lg:sticky lg:top-8">
            <div className="bg-white rounded-3xl shadow-xl shadow-emerald-100/60 border border-emerald-100 p-5 sm:p-7"
                  data-testid="home-quote-card">
              <div className="text-xs font-extrabold uppercase tracking-[0.18em] text-emerald-800">
                Get a quick and easy price
              </div>
              <div className="mt-1 text-xl sm:text-2xl font-extrabold text-gray-900 font-[Outfit] tracking-tight">
                Step {step === 4 ? "4" : step} of 4
                {step === 1 && " · Address"}
                {step === 2 && " · Contact"}
                {step === 3 && " · Service"}
                {step === 4 && " · Your price"}
              </div>
              <div className="mt-3 h-1.5 rounded-full bg-emerald-100 overflow-hidden">
                <div className="h-full bg-emerald-600 transition-all"
                      style={{ width: `${(step / 4) * 100}%` }} />
              </div>

              {/* STEP 1 — Address */}
              {step === 1 && (
                <div className="mt-5 space-y-4" data-testid="quote-step-1">
                  <AddressAutocomplete
                    id="home-quote-address"
                    label="Enter your street address"
                    value={addr}
                    onChange={(v) => {
                      setAddr(v);
                      // iter240 — If the customer edits over a previously
                      // selected address, invalidate the stale coords/sqft
                      // so the satellite loader doesn't display a mismatched
                      // property. They can re-select from the dropdown to
                      // supply fresh coords, or continue with just the
                      // typed address (loader falls back to MapPin icon).
                      if (addrDetails?.formatted_address
                          && v !== addrDetails.formatted_address
                          && v !== addrDetails.address) {
                        setAddrDetails(null);
                      }
                    }}
                    onPlace={(p) => {
                      if (p?.address) setAddr(p.formatted_address || p.address);
                      setAddrDetails(p);
                    }}
                    testId="home-quote-address"
                    placeholder="123 Main St, Jacksonville, FL"
                    showInlineSummary={false}
                  />
                  <Button
                    className="w-full h-12 rounded-full font-extrabold bg-emerald-700 hover:bg-emerald-800 text-white"
                    disabled={!step1Valid}
                    onClick={() => setStep(2)}
                    data-testid="quote-next-step-1"
                  >
                    Continue <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                  <p className="text-[11px] text-gray-500 flex items-center gap-1">
                    <Lock className="w-3 h-3" /> Your address is used only to
                    quote and dispatch service.
                  </p>
                </div>
              )}

              {/* STEP 2 — Contact */}
              {step === 2 && (
                <div className="mt-5 space-y-3" data-testid="quote-step-2">
                  <div>
                    <Label htmlFor="home-quote-name">Name</Label>
                    <Input id="home-quote-name" value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Full name" className="h-11"
                            data-testid="home-quote-name" />
                  </div>
                  <div>
                    <Label htmlFor="home-quote-phone">Phone</Label>
                    <Input id="home-quote-phone" value={phone} type="tel"
                            inputMode="tel"
                            onChange={(e) => setPhone(e.target.value)}
                            placeholder="(904) 555-1234" className="h-11"
                            data-testid="home-quote-phone" />
                  </div>
                  <div>
                    <Label htmlFor="home-quote-email">Email</Label>
                    <Input id="home-quote-email" value={email} type="email"
                            inputMode="email"
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="you@example.com" className="h-11"
                            data-testid="home-quote-email" />
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button variant="outline"
                             className="h-11 rounded-full font-semibold flex-1"
                             onClick={() => setStep(1)}
                             data-testid="quote-back-step-2">
                      Back
                    </Button>
                    <Button
                      className="h-11 rounded-full font-extrabold flex-1 bg-emerald-700 hover:bg-emerald-800 text-white"
                      disabled={!step2Valid} onClick={() => setStep(3)}
                      data-testid="quote-next-step-2"
                    >
                      Continue <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}

              {/* STEP 3 — Service + frequency + notes */}
              {step === 3 && (
                <div className="mt-5 space-y-3" data-testid="quote-step-3">
                  <div>
                    <Label>Service needed</Label>
                    <div className="mt-1 grid grid-cols-2 gap-2">
                      {SERVICES.map((s) => {
                        const active = serviceKey === s.key;
                        return (
                          <button key={s.key} type="button"
                                    onClick={() => setServiceKey(s.key)}
                                    className={`text-left p-3 rounded-xl border transition ${active ? "border-emerald-600 bg-emerald-50 ring-2 ring-emerald-600/25" : "border-gray-200 bg-white hover:border-emerald-300"}`}
                                    data-testid={`quote-service-${s.key}`}>
                            <s.icon className={`w-5 h-5 ${active ? "text-emerald-700" : "text-gray-600"}`} />
                            <div className={`mt-1 text-[13px] font-extrabold leading-tight ${active ? "text-emerald-800" : "text-gray-900"}`}>
                              {s.name}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <Label>How often?</Label>
                    <div className="mt-1 grid grid-cols-2 gap-2">
                      {FREQ_OPTIONS.map((f) => {
                        const active = frequency === f.key;
                        return (
                          <button key={f.key} type="button"
                                    onClick={() => setFrequency(f.key)}
                                    className={`p-2.5 rounded-xl border text-[13px] font-extrabold transition ${active ? "border-emerald-600 bg-emerald-50 text-emerald-800 ring-2 ring-emerald-600/25" : "border-gray-200 bg-white text-gray-700 hover:border-emerald-300"}`}
                                    data-testid={`quote-freq-${f.key}`}>
                            {f.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {/* iter236 — Choose service date */}
                  <div>
                    <Label htmlFor="home-quote-date">Choose your service date</Label>
                    <div className="mt-1 relative">
                      <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-700 pointer-events-none" />
                      <Input
                        id="home-quote-date"
                        type="date"
                        min={_todayIso}
                        value={serviceDate}
                        onChange={(e) => setServiceDate(e.target.value)}
                        className="h-11 pl-9 font-semibold"
                        data-testid="home-quote-date"
                      />
                    </div>
                    <p className="mt-1 text-[11px] text-gray-500">
                      {frequency === "one_time"
                        ? "We\u2019ll service your yard on this date."
                        : `First visit on this date. Future visits repeat ${
                            FREQ_OPTIONS.find((f) => f.key === frequency)?.label.toLowerCase()
                          } from here.`}
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="home-quote-notes">
                      Any additional info? <span className="text-gray-400 font-normal">(optional)</span>
                    </Label>
                    <Textarea id="home-quote-notes" value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="Gate code, dog, tricky spots…"
                                rows={3} data-testid="home-quote-notes" />
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button variant="outline"
                             className="h-12 rounded-full font-semibold flex-1"
                             onClick={() => setStep(2)}
                             data-testid="quote-back-step-3">
                      Back
                    </Button>
                    <Button
                      className="h-12 rounded-full font-extrabold flex-1 bg-emerald-700 hover:bg-emerald-800 text-white text-base tracking-wide"
                      disabled={submitting}
                      onClick={submitQuote}
                      data-testid="quote-see-my-price"
                    >
                      {submitting
                        ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Calculating…</>
                        : <>SEE MY PRICE <ArrowRight className="w-4 h-4 ml-1" /></>}
                    </Button>
                  </div>
                </div>
              )}

              {/* STEP 4 — Result */}
              {step === 4 && result && (
                <div className="mt-4 animate-in" data-testid="quote-step-4">
                  <div className="rounded-2xl bg-gradient-to-br from-emerald-600 to-emerald-800 text-white p-4 sm:p-6 shadow-[0_20px_60px_rgba(4,120,87,0.35)]">
                    <div className="text-[11px] uppercase tracking-[0.18em] font-extrabold opacity-90">
                      Your instant quote
                    </div>
                    <div className="mt-1 flex items-baseline gap-1 flex-wrap">
                      <span className="text-4xl sm:text-5xl font-extrabold leading-none tracking-tight font-[Outfit]"
                             data-testid="quote-price-amount">
                        {result.quote_amount != null
                          ? `$${Number(result.quote_amount).toFixed(2)}`
                          : (result.quote_range
                              ? `$${result.quote_range.from.toFixed(0)}–$${result.quote_range.to.toFixed(0)}`
                              : "Quote sent")}
                      </span>
                      <span className="text-sm opacity-90 font-semibold"
                             data-testid="quote-price-cadence">
                        {result.frequency && result.frequency !== "one_time"
                          ? "per visit"
                          : "one-time"}
                      </span>
                    </div>
                    <div className="mt-2 text-sm opacity-90 font-semibold"
                          data-testid="quote-price-service">
                      {activeSvc?.name || result.service_name}
                    </div>
                    <div className="mt-0.5 text-[12px] opacity-80 truncate"
                          data-testid="quote-price-address">
                      {addr}
                    </div>
                  </div>
                  <Button
                    className="mt-3 w-full h-12 rounded-full font-extrabold bg-emerald-700 hover:bg-emerald-800 text-white shadow-[0_8px_24px_rgba(4,120,87,0.28)]"
                    onClick={continueToBook}
                    data-testid="quote-continue-to-book"
                  >
                    Continue to book <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                  <button
                    type="button"
                    className="mt-2 w-full text-sm text-gray-600 hover:text-gray-900 font-semibold py-1"
                    onClick={() => {
                      setStep(1); setResult(null);
                    }}
                    data-testid="quote-start-over"
                  >
                    Get another quote
                  </button>
                  <p className="mt-3 text-[11px] text-gray-500 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3 text-emerald-600 shrink-0" />
                    Saved — we&apos;ll email you if you don&apos;t book right away.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 mt-16">
        <div className="text-xs uppercase tracking-[0.2em] text-emerald-800 font-bold">
          Every yard service
        </div>
        <h2 className="mt-1 text-3xl sm:text-4xl font-extrabold text-gray-900 font-[Outfit] tracking-tight">
          JDSN Lawn Care Services
        </h2>
        <p className="mt-2 text-gray-600 max-w-2xl">
          One trusted crew, every service your property needs. Book any of
          these directly through the JDSN customer portal.
        </p>
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4"
              data-testid="home-services-grid">
          {SERVICES.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => {
                setServiceKey(s.key);
                setStep(1);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className="group text-left bg-white rounded-2xl border border-gray-200 p-4 hover:border-emerald-500 hover:shadow-lg hover:shadow-emerald-100/60 transition"
              data-testid={`home-service-card-${s.key}`}
            >
              <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center group-hover:bg-emerald-600 group-hover:text-white transition">
                <s.icon className="w-5 h-5" />
              </div>
              <div className="mt-3 text-base font-extrabold text-gray-900">{s.name}</div>
              <div className="mt-0.5 text-[13px] text-gray-500 leading-snug">
                {s.blurb}
              </div>
              <div className="mt-3 inline-flex items-center gap-1 text-[12px] font-extrabold text-emerald-700 group-hover:text-emerald-800">
                Get a price <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 mt-16">
        <div className="grid sm:grid-cols-3 gap-4">
          {[
            { n: "01", h: "Enter your address", d: "We check your property so we can quote accurately in seconds." },
            { n: "02", h: "See your price",     d: "Instant lawn mowing quotes. Every other service gets a fast pricing range." },
            { n: "03", h: "Book & relax",       d: "Card checkout, receipts, and Pay After Service — all inside the customer portal." },
          ].map((s) => (
            <div key={s.n} className="rounded-2xl border border-gray-200 bg-white p-5">
              <div className="text-[11px] uppercase tracking-[0.2em] font-extrabold text-emerald-800">
                {s.n}
              </div>
              <div className="mt-1 text-xl font-extrabold text-gray-900">{s.h}</div>
              <div className="mt-2 text-[13px] text-gray-600 leading-relaxed">{s.d}</div>
            </div>
          ))}
        </div>
        <div className="mt-8 text-center">
          <Link to="/reviews"
                 className="inline-flex items-center gap-2 text-emerald-800 font-extrabold hover:underline"
                 data-testid="home-see-reviews">
            <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
            See what Jacksonville is saying
          </Link>
        </div>
      </section>
    </div>
  );
}
